/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosProgressEvent } from 'axios';
import { getApi } from '../common/requestHelper';
import {
  GlossaryExtractionApi,
  GlossaryExtractionOptionDtoLanguagesEnum,
  GlossaryExtractionResponseItemDtoStatusEnum,
  GetGlossaryExtractionResponseDto,
} from '@ink-ai/insight-service-sdk';
import { getStore, RootState } from '.';
import { downloadFromLink } from '../common/utils';

export const languageOptions = new Map<
  GlossaryExtractionOptionDtoLanguagesEnum,
  string
>([
  [GlossaryExtractionOptionDtoLanguagesEnum.ZhCn, 'Chinese (Simplified)'],
  [GlossaryExtractionOptionDtoLanguagesEnum.EnUs, 'English'],
  [GlossaryExtractionOptionDtoLanguagesEnum.Ja, 'Japanese'],
]);

export const statusStringMapping: Record<
  GlossaryExtractionResponseItemDtoStatusEnum,
  string
> = {
  [GlossaryExtractionResponseItemDtoStatusEnum.PendingUpload]:
    'glossaryExtraction.status.pendingUpload',
  [GlossaryExtractionResponseItemDtoStatusEnum.Queueing]: 'glossaryExtraction.status.queueing',
  [GlossaryExtractionResponseItemDtoStatusEnum.Extracting]: 'glossaryExtraction.status.extracting',
  [GlossaryExtractionResponseItemDtoStatusEnum.Success]: 'glossaryExtraction.status.succeeded',
  [GlossaryExtractionResponseItemDtoStatusEnum.Error]: 'glossaryExtraction.status.failed',
};

export const statusClassMapping: Record<
  GlossaryExtractionResponseItemDtoStatusEnum,
  string
> = {
  [GlossaryExtractionResponseItemDtoStatusEnum.PendingUpload]: 'status-info',
  [GlossaryExtractionResponseItemDtoStatusEnum.Queueing]: 'status-info',
  [GlossaryExtractionResponseItemDtoStatusEnum.Extracting]: 'status-info',
  [GlossaryExtractionResponseItemDtoStatusEnum.Success]: 'status-success',
  [GlossaryExtractionResponseItemDtoStatusEnum.Error]: 'status-error',
};

export interface GlossaryExtractionState {
  mode: 'Documents';
  languages: GlossaryExtractionOptionDtoLanguagesEnum[];
  selectedLanguages: GlossaryExtractionOptionDtoLanguagesEnum[];
  fileName: string;
  fileNames: {};
  fileSizes: {};
  glossaryExtractionStatus:
    | 'waiting upload'
    | 'uploading'
    | 'queueing'
    | 'extracting'
    | 'completed'
    | 'failed';
  progress: number;
  uuid: string | null;
  uploadUrls: string[] | null;
  instanceId: string | null;
  loading: boolean;
  errorMessage: string;
  selectedFiles?: {}
  timestamp: number; // Used to tell the action whether the job is outdated
  sourceText?: string;
}

const initialState: GlossaryExtractionState = {
  mode: 'Documents',
  languages: [
    GlossaryExtractionOptionDtoLanguagesEnum.EnUs,
    GlossaryExtractionOptionDtoLanguagesEnum.ZhCn,
  ], // languages that can be displayed on the language tab
  selectedLanguages: [],
  selectedFiles: {},
  fileName: '',
  fileNames: {},
  fileSizes: {},
  glossaryExtractionStatus: 'waiting upload',
  progress: 0,
  uuid: null,
  uploadUrls: [],
  instanceId: null,
  loading: false,
  errorMessage: '',
  timestamp: 0,
  sourceText: '',
};

export const handleDownload = async (id: string, suggestedName?: string) => {
  const store = getStore();
  try {
    store.dispatch(glossaryExtractionActions.setLoading(true));
    const extractGlossaryApi = await getApi(GlossaryExtractionApi);
    const response = await extractGlossaryApi.getGlossaryExtractionDownloadLink(id);
    await downloadFromLink(response.data.downloadLink, suggestedName);
  } catch (error: any) {
    console.error('Failed to download the file:', error);
  } finally {
    store.dispatch(glossaryExtractionActions.setLoading(false));
  }
};

export const getGlossaryExtractionFile = async (id: string): Promise<{ file: File, fileName: string } | null> => {
  const store = getStore();
  try {
    store.dispatch(glossaryExtractionActions.setLoading(true));
    const extractGlossaryApi = await getApi(GlossaryExtractionApi);
    const response = await extractGlossaryApi.getGlossaryExtractionDownloadLink(id);
    
    // 获取文件内容
    const fileResponse = await fetch(response.data.downloadLink);
    const blob = await fileResponse.blob();
    
    // 从URL中提取文件名
    const url = new URL(response.data.downloadLink);
    const pathParts = url.pathname.split('/');
    const fileName = pathParts[pathParts.length - 1];
    
    // 创建File对象
    const file = new File([blob], fileName, { type: 'text/csv' });
    
    return { file, fileName };
  } catch (error: any) {
    console.error('Failed to get the file:', error);
    return null;
  } finally {
    store.dispatch(glossaryExtractionActions.setLoading(false));
  }
};

export const uploadAndExtractGlossary = createAsyncThunk<
  void,
  { name?: string; description?: string } | undefined
>(
  'glossaryExtraction/uploadAndExtractGlossary',
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const instanceId = state.auth.instanceId;
      const initTimestamp = state.glossaryExtraction.timestamp;
      dispatch(glossaryExtractionActions.setInstanceId(instanceId));

      // Get the presigned URL
      dispatch(glossaryExtractionActions.setGlossaryExtractionStatus('uploading'));
      const extractGlossaryApi = await getApi(GlossaryExtractionApi);

      const fileNamesArray = Object.values(state.glossaryExtraction.fileNames).filter(name => name);
      // 将这些值以逗号分隔的形式连接起来
      const combinedFileNames = fileNamesArray.join(',');

      const response = await extractGlossaryApi.createGlossaryExtractionForUpload({
        filename: combinedFileNames,
        instanceId: instanceId,
        option: {
          languages: state.glossaryExtraction.languages,
        },
        // include optional name/description from UI
        name: payload?.name,
        description: payload?.description,
      });
      const { uuid, uploadUrls } = response.data;
      dispatch(glossaryExtractionActions.setUuid(uuid));

      // Upload the files to S3
      for (let i = 0; i < uploadUrls.length; i++) {
        const file = state.glossaryExtraction.selectedFiles[uploadUrls[i].language];
        if (!file) continue;
        await axios.put(uploadUrls[i].uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1),
            );
            const currState = getState() as RootState;
            if (initTimestamp !== currState.glossaryExtraction.timestamp) {
              return;
            }
            dispatch(glossaryExtractionActions.setProgress(progress));
          },
        });
      }

      // Start the glossary extraction
      const currState = getState() as RootState;
      if (initTimestamp !== currState.glossaryExtraction.timestamp) {
        console.warn('The job is outdated, aborting');
        return;
      }
      dispatch(glossaryExtractionActions.setGlossaryExtractionStatus('queueing'));
      await extractGlossaryApi.startGlossaryExtraction(uuid, {});
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Failed to extract glossary';
      // 回退前端状态，允许重新上传
      dispatch(glossaryExtractionActions.setGlossaryExtractionStatus('waiting upload'));
      dispatch(glossaryExtractionActions.setLoading(false));
      return rejectWithValue({ status, message });
    }
  },
);

export const refreshGlossaryExtractionStatus = createAsyncThunk(
  'glossaryExtraction/refreshGlossaryExtractionStatus',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const extractGlossaryApi = await getApi(GlossaryExtractionApi);
    const response = await extractGlossaryApi.getGlossaryExtraction(
      state.glossaryExtraction.uuid,
    );
    dispatch(glossaryExtractionActions.updateGlossaryExtractionResult(response.data));
  },
);

export const glossaryExtraction = createSlice({
  name: 'glossaryExtraction',
  initialState,
  reducers: {
    updateGlossaryExtractionResult(
      state,
      action: PayloadAction<GetGlossaryExtractionResponseDto>,
    ) {
      if (action.payload.uuid !== state.uuid) {
        console.log(
          'The glossary extraction job is outdated, aborting websocket message',
        );
        return;
      }
      if (action.payload.status === 'ERROR') {
        state.glossaryExtractionStatus = 'failed';
        state.loading = false;
        state.errorMessage = action.payload.errorMessage;
      } else if (action.payload.status === 'SUCCESS') {
        state.glossaryExtractionStatus = 'completed';
        state.loading = false;
      }
      state.errorMessage = action.payload.errorMessage;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setGlossaryExtractionStatus(
      state,
      action: PayloadAction<GlossaryExtractionState['glossaryExtractionStatus']>,
    ) {
      state.glossaryExtractionStatus = action.payload;
    },
    setMode(state, action: PayloadAction<'Documents'>) {
      state.mode = action.payload;
    },
    setSelectedLanguages(state, action: PayloadAction<GlossaryExtractionOptionDtoLanguagesEnum[]>) {
      state.selectedLanguages = action.payload;
    },

    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
    },
    setInstanceId(state, action: PayloadAction<string>) {
      state.instanceId = action.payload;
    },
    clearAll: () => {
      return {
        ...initialState,
        timestamp: Date.now(),
      };
    },    
    setFileName(state, action: PayloadAction<string>) {
      state.fileName = action.payload;
    },
    // Set the selected file for a specific language
    setSelectedFileForLanguage(state, action: PayloadAction<{ language: string; file: File | null }>) {
      const { language, file } = action.payload;
      state.selectedFiles[language] = file;  // Store file by language
    },

    // Set the file name for a specific language
    setFileNameForLanguage(state, action: PayloadAction<{ language: string; fileName: string }>) {
      const { language, fileName } = action.payload;
      state.fileNames[language] = fileName;  // Store file name by language
    },

    // Set the file size for a specific language
    setFileSizeForLanguage(state, action: PayloadAction<{ language: string; fileSize: number }>) {
      const { language, fileSize } = action.payload;
      state.fileSizes[language] = fileSize;  // Store file size by language
    },
    setUuid(state, action: PayloadAction<string>) {
      state.uuid = action.payload;
    },
    setTimeStamp(state, action: PayloadAction<number | undefined>) {
      state.timestamp = action.payload ?? Date.now();
    },
    removeFileForLanguage(state, action: PayloadAction<{ language: string}>) {
      const { language } = action.payload;
      state.selectedFiles[language] = undefined;
      state.fileNames[language] = '';
      state.fileSizes[language] = '';
      state.glossaryExtractionStatus = 'waiting upload';
    },
    setSourceText(state, action: PayloadAction<string>) {
      state.sourceText = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadAndExtractGlossary.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadAndExtractGlossary.rejected, (state, action) => {
        state.loading = false;
        state.glossaryExtractionStatus = 'failed';
        const payload: any = action.payload as any;
        if (payload?.message) {
          state.errorMessage = payload.message;
        } else if (action.error?.message) {
          state.errorMessage = action.error.message as string;
        }
      });
  },
});

export const glossaryExtractionActions = glossaryExtraction.actions;
export default glossaryExtraction.reducer;
